import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import hubLayout from 'styles/css/hubLayout.module.css';
import { ACCESS_TOKEN_NAME, checkUserPw, decryptAES256, encryptSHA256, getSubUserByAccToken, getUserByAccToken, textEncryptAes256, updateSubUser, updateUserInfo } from 'api/api';
import { useModal } from 'components/common/ModalLayout';
import { useForm } from 'react-hook-form';
import { SubUser, UserInfo } from 'type/type';
import { AddrInputComponent, EmailInputField, IdInputField, MobileInputField, PwCheckInputField, PwInputField } from 'components/common/InputField';
import { NiceUi, onClickNicePass } from 'components/nice/NiceUi';
import { SubUserLoginInfoContext, UserLoginInfoContext } from 'pages/Layout';
import { webviewClose } from 'api/bridgeUser';

export function UserInfoEdit() {
  /* 프로세스 (컨펌 필요함)
  1. 비밀번호 입력 및 확인
  2. 사용자 정보 수정 가능
    1) 비밀번호는 현재 비밀번호 입력해야 수정가능
  */
  // ****************************** 1. props, hook
  const redirect_code = new URLSearchParams(window.location.search).get('redirect_code');

  // Nice UI에서 넘어온 경우, 비번 인증 없이 회원정보 페이지로 바로 가기.
  const valid_pw = new URLSearchParams(window.location.search).get('valid_pw');

  
  const navigate = useNavigate();
  const { userLoginInfo, setUserLoginInfo } = useContext(UserLoginInfoContext);
  const { subUserLoginInfo, setSubUserLoginInfo } = useContext(SubUserLoginInfoContext);
  const { showModal } = useModal();
  const [user, setUser] = useState<UserInfo|null>(userLoginInfo);	
  const [subUser, setSubUser] = useState<SubUser|null>(subUserLoginInfo);	
  const [validPw, setValidPw] = useState<boolean>(valid_pw === 'true' ? true : false);
  const [isChangePw, setIsChangePw] = useState<boolean>(subUser ? true : false);
  // const [validPw, setValidPw] = useState<boolean>(false);
  // useForm
  const { register, formState: { errors }, setValue, trigger, watch } = useForm<any>({
    mode: 'all',
  });
  const passwordWatch = watch("password");

  // console.log('errors', errors);
  // console.log('isValid', isValid);  // ****************************** 3. api
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (user) {
      setUser((prevData) => ({
        ...prevData!,
        [name]: value,
      } as UserInfo)); // 👈 명시적으로 타입 지정
    } else if (subUser) {
      setSubUser((prevData) => ({
        ...prevData!,
        [name]: value,
      } as SubUser));
    }
  };

  

  const getUserInfo = async () => { 
    if (user) {
      const response = await getUserByAccToken();
      if (response.code === 200) {
        const { REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV } = process.env;
        setUserLoginInfo({
          ...response.data,
          mobile: decryptAES256(response.data.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!),
          password: '',
        });
      } 
    } else {
      const response = await getSubUserByAccToken();
      if (response.code === 200) {
        setSubUserLoginInfo(response.data);
      }
    }
  }

  // 완료
  const checkPw = async () => {
    const rawPassword = user?.password || subUser?.password;
    if (!rawPassword) return;

    const data = {
      password: textEncryptAes256(encryptSHA256(rawPassword)),
    }
    const checkRes = await checkUserPw(data);
    if (checkRes.code === 200 ) {
      await getUserInfo();
      setValidPw(true);
      navigate('/user/info/edit?valid_pw=true');
    } else {
      showModal('alert', '비밀번호가 일치하지 않습니다.');
    }
  };

  const updateUser = async () => {
    const target = user ?? subUser;
  
    if (!target) return showModal('alert', '사용자 정보가 없습니다.');
  
    const data: any = {
      ...target,
    };
  
    // user일 경우에만 mobile 암호화 추가
    if (user?.mobile) {
      data.mobile = textEncryptAes256(user.mobile);
    }
  
    // 비밀번호 변경 시 처리
    if (isChangePw) {
      if (!target.password) {
        return showModal('alert', '변경할 비밀번호를 입력해주세요.');
      }
      if (target.password !== target.passwordcheck) {
        return showModal('alert', '비밀번호 확인을 동일하게 입력해주세요.');
      }
      data.password = textEncryptAes256(target.password);
    } else {
      if (!data.password) delete data.password;
    }
  
    // passwordcheck는 항상 제거
    delete data.passwordcheck;
    
    const userRes = user ? await updateUserInfo(data) : await updateSubUser(data);
  
    if (userRes.code === 200) {
      showModal('alert', '회원정보 수정이 완료되었습니다.', { confirm: true, cancel: true },
        webviewClose,
        webviewClose,
      );
      getUserInfo();
    } else {
      showModal('alert', userRes.message);
    }
  };

  // ****************************** 5. useEffect
  useEffect(() => {
    // NicePass 인증 결과 처리
    if (redirect_code === 'user_found') {
      showModal('alert', '본인인증이 완료되었습니다.');
    }
  }, []);
  const isFirstRender = useRef(0);
  useEffect(() => {
    if (isFirstRender.current < 2) {
      isFirstRender.current = isFirstRender.current + 1;
      return; // 첫 렌더링에서는 트리거 안 함
    }
    // password 값이 바뀌면 passwordcheck의 유효성 재검사
    trigger("passwordcheck");
  }, [passwordWatch]);
  return (
    <>
      <div className={`${hubLayout.cnt_box}`} style={{overflowY: "auto"}}>
        <div className={`${hubLayout.acc__cnt}`}>
          {!(redirect_code || validPw) && <>
            {/* <div className={`${hubLayout.sec_title} ${hubLayout.fixed_title}`}>
              <h2>비밀번호 확인</h2>
            </div> */}
            <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>비밀번호 확인이 필요합니다.</h2>
            <ul className={`${hubLayout.inp_list}`}>
              {/* 아래는 PwInputField 넣을시, 아래 폼이랑 충돌나서 이대로 씀. */}
              <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                <label className={`${hubLayout.imp}`}>Password</label>
                <input name='password' type='password' placeholder='Password' onChange={handleChange} value={user?.password ?? subUser?.password}/>
              </li>
            </ul>
            <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
              <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={checkPw} disabled={!(user?.password ?? subUser?.password)}>조회</button>
            </div>
          </>}
          {(redirect_code || validPw) && <>
            {/* <div className={`${hubLayout.sec_title} ${hubLayout.fixed_title}`}>
              <h2>회원정보 변경</h2>
            </div> */}
            <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>회원 정보</h2>
            <ul className={`${hubLayout.inp_list}`}>
              <IdInputField value={user ?? subUser} register={register} registerOptions={{ required: true }} errors={errors} readOnly />
              <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                <label className={`${hubLayout.imp}`}>이름</label>
                <input type='text' placeholder='이름' className={`${errors.name && hubLayout.border_red}`} value={user?.name ?? subUser?.name} readOnly
                  {...register(`name`, {
                    required: true,
                  })}
                  style={{width: user && false ? '70%' : '100%'}}
                />
                {user && false &&
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} 
                    onClick={() => onClickNicePass({ type:'change_userinfo', redirect_url: {success: '/user/info/edit', fail: '/user/info/edit'}, access_token: localStorage.getItem(ACCESS_TOKEN_NAME) as string })}
                    style={{width: '30%'}}>변경</button>
                }
              </li>
              {user &&
                <>
                  <MobileInputField value={user} register={register} registerOptions={{ required: true }} errors={errors} readOnly />
                  <div className={`${hubLayout.btn_group} ${hubLayout.back_white}`}>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => setIsChangePw((prev) => !prev)}>
                      {isChangePw ? '비밀번호 변경 취소' : '비밀번호 변경'}
                    </button>
                  </div>
                </>
              }
              {isChangePw &&
                <>
                  <PwInputField value={user ?? subUser} register={register} registerOptions={{ required: false, onChange: handleChange }} errors={errors} />
                  <PwCheckInputField value={user ?? subUser} register={register} registerOptions={{ required: false, onChange: handleChange }} errors={errors} />
                </>
              }
              {/* <NameInputField value={user} register={register} registerOptions={{ required: true }} errors={errors} readOnly /> */}
              {user &&
                <>
                  <EmailInputField value={user} register={register} registerOptions={{ required: true, onChange: handleChange }} errors={errors} />
                  <AddrInputComponent name={'address'} title={'주소'} user={user} handleChange={handleChange} register={register} errors={errors} setValue={setValue} />
                </>
              }
            </ul>
            <div style={{display:'none'}}>
              <NiceUi />
            </div>
            <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
              {/* <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={()=>logout()}>취소</button> */}
              {/* isValid 자꾸 false로 나와서 errors 객체씀. */}
              <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={updateUser} disabled={Object.keys(errors).length !== 0}>수정</button>
            </div>
          </>}
        </div>
      </div>
    </>
  );
}